import {patchState, signalStore, withComputed, withMethods, withState} from "@ngrx/signals";
import {withDevtools} from "@angular-architects/ngrx-toolkit";
import {computed, inject} from "@angular/core";
import {ApiService} from "../../../shared/services/api.service";
import {take} from "rxjs";
import {getEndOfWeek, getMonthsBetweenDates, getNextMonthEndDate, getPreviousMonthStartDate, getStartOfWeek, getYearEndDate, getYearStartDate} from "../mapper/calendar-helper.mapper";
import {EventSettingsModel, View, WorkHoursModel} from "@syncfusion/ej2-angular-schedule";
import {LocaleService} from "../../../shared/services/locale.service";
import {CalendarEventResource} from "../../../shared/types/calendar-types/calendarEventResource";
import {CalendarPrioritySumResource} from "../../../shared/types/calendar-types/calendarPrioritySumResource";
import {CalendarResource} from "../../../shared/types/calendar-types/calendarResource";
import {LanguageService} from "../../../shared/services/language.service";
import {CalendarEventPriority} from "../../../shared/types/calendar-types/calendarEventPriority";
import {TYPE} from "../../../shared/types/types";
import {mapCalendarEventResourceToSyncfusionEvent} from "../mapper/calendar-to-syncfusion.mapper";
import {Query} from '@syncfusion/ej2-data';
import {CalendarEventUpdateRequest} from "../../../shared/types/calendar-types/calendarEventUpdateRequest";
import {createCalendarEventResourceSeriesOverrideEvent, mapCalendarEventResourceToCalendarEventUpdateRequest} from "../mapper/calendar-api.mapper";
import {ProfileService} from "../../../shared/services/profile.service";
import {CapabilitiesService} from "../../../shared/services/capabilities.service";

export interface CalendarState {
  calendars: CalendarResource[],
  events: CalendarEventResource[],
  preLoadedMonths: Record<number, string[]>,
  selectedCalendarID: number | null,
  schedulerOptions: {
    selectedDate: Date,
    view: View,
    workHours: WorkHoursModel,
    workDays: number[],
    defaultNotificationTime: number,
  },
  widgetScheduler: {
    selectedDate: Date,
    upcomingEvents: CalendarEventResource[],
    weekEvents: Map<string, CalendarPrioritySumResource>,
  },
  translate: boolean
}

const initialState: CalendarState = {
  calendars: [],
  events: [],
  preLoadedMonths: {},
  selectedCalendarID: null,
  schedulerOptions: {
    selectedDate: new Date(),
    view: 'Day',
    workHours: {
      start: '00:00',
      end: '24:00',
      highlight: true,
    },
    workDays: [1, 2, 3, 4, 5],
    defaultNotificationTime: 5
  },
  widgetScheduler: {
    selectedDate: new Date(),
    upcomingEvents: [],
    weekEvents: new Map<string, CalendarPrioritySumResource>(),
  },
  translate: false,
};

export const CalendarStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withDevtools('CalendarStore'),
  withMethods((store) => {
    const api: ApiService = inject(ApiService);
    const locale: LocaleService = inject(LocaleService);
    const profile: ProfileService = inject(ProfileService);
    const capabilities: CapabilitiesService = inject(CapabilitiesService);

    return {
      // #region general
      initStore(){
        const selectedDateBeforeInit = store.schedulerOptions.selectedDate();
        this.resetStore();
        this.setSelectedDate(selectedDateBeforeInit);
        if (!capabilities.hasCalendarRight()) {
          return;
        }
        this.loadCalendars();
        
        // Restore view from cookie
        const savedView = this.getViewFromCookie();
        if (savedView) {
          patchState(store, (state) => ({
            ...state,
            schedulerOptions: {
              ...state.schedulerOptions,
              view: savedView as View
            },
            translate: !!(profile.profile()?.user.auto_translation ?? false)
          }));
        }
      },
      resetStore() {
        const now = new Date();
        patchState(store, {
          ...initialState,
          schedulerOptions: {
            ...initialState.schedulerOptions,
            selectedDate: now,
          },
          widgetScheduler: {
            ...initialState.widgetScheduler,
            selectedDate: now,
            weekEvents: new Map<string, CalendarPrioritySumResource>(),
          },
        });
      },
      setTranslate(translate: boolean){
        if(store.translate() !== translate) {
          patchState(store, (state) => 
            ({
              ...state,
              translate: translate
            })
          );
        }
      },
      // #endregion general
      // #region calendars
      loadCalendars(){
        api.getCalendars().pipe(
          take(1),
        ).subscribe({
          next: (calendars) => {
            this.setCalendars(calendars);
          },
          error: (error) => {
            console.error('Error loading calendar list: ', error);
          }
        })
      },
      setCalendars(calendars: CalendarResource[]){
        patchState(store, (state) => 
        ({
          ...state,
          calendars: calendars,
          selectedCalendarID: calendars.length > 0 ? calendars[0].id : null
        })
        );
        this.evaluatePreLoading();
        this.evaluateWidgetUpcomingEvents();
        this.evaluateWidgetWeekEvents();
      },
      setSelectedCalendarID(calendarID: number){
        patchState(store, (state) => 
        ({
          ...state,
          selectedCalendarID: calendarID
        })
        );
      },
      // #endregion calendars
      // #region events
      loadCalendarEvents(start: Date, end: Date){
        store.calendars().forEach((calendar) => {
          api.getCalendarEvents(calendar.id, start.toISOString(), end.toISOString())
          .pipe(
            take(1),
          )
          .subscribe({
            next: (events) => {
              this.updateEvents(events);
              this.updatePreLoadedMonths(calendar.id, start, end);
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
              }
            })
      })
      },
      loadCalendarEventsForCalendars(calendarIds: number[], start: Date, end: Date): void {
        calendarIds.forEach((calendarId) => {
          api.getCalendarEvents(calendarId, start.toISOString(), end.toISOString())
          .pipe(
            take(1),
          )
          .subscribe({
            next: (events) => {
              this.updateEvents(events);
              this.updatePreLoadedMonths(calendarId, start, end);
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
              }
            })
        });
      },
      getVisibleSchedulerRange(): {start: Date, end: Date} {
        if(store.schedulerOptions.view() === 'Year') {
          return {
            start: getYearStartDate(store.schedulerOptions.selectedDate()),
            end: getYearEndDate(store.schedulerOptions.selectedDate())
          };
        }
        return {
          start: getPreviousMonthStartDate(store.schedulerOptions.selectedDate()),
          end: getNextMonthEndDate(store.schedulerOptions.selectedDate())
        };
      },
      getSelectedSchedulerCalendarIds(): number[] {
        const selectedCalendarId = store.selectedCalendarID();
        if (selectedCalendarId !== null) {
          return [selectedCalendarId];
        }
        return store.calendars().map(calendar => calendar.id);
      },
      refreshVisibleSchedulerRangeForSelectedCalendars(): void {
        const calendarIds = this.getSelectedSchedulerCalendarIds();
        if (calendarIds.length === 0) {
          return;
        }
        const {start, end} = this.getVisibleSchedulerRange();
        this.loadCalendarEventsForCalendars(calendarIds, start, end);
      },
      setEvents(events: CalendarEventResource[]){
        patchState(store, (state) => 
        ({
          ...state,
          events: events
        })
        )
      },
      updateEvents(events: CalendarEventResource[]) {
        const eventIds = new Set(events.map((e) => e.id));
        const stateEvents = store.events().filter((event) => 
          !eventIds.has(event.id) && !eventIds.has(event.override.parent_id ?? -1)
        );
        const eventsToAdd = events.filter((newEvent) => newEvent.calendar_id !== null);

        patchState(store, (state) => ({
          ...state,
          events: [...stateEvents, ...eventsToAdd],
        }));
      },
      removeEvents(ids: number[]) {
        patchState(store, (state) => 
        ({
          ...state,
          events: state.events.filter(event => !ids.includes(event.id))
        })
        )
      },
      getEventById(id: number): CalendarEventResource | undefined {
        return store.events().find(event => event.id === id);
      },
      filterEvents(predicate: (e: CalendarEventResource) => boolean): CalendarEventResource[] {
        return store.events().filter(predicate);
      },
      createEvent(event: CalendarEventUpdateRequest){
        api.createOrUpdateCalendarEvent(event, null, '', false, false, false)
        .pipe(
          take(1),
        )
        .subscribe({
          next: (events) => {
            this.updateEvents(events);
            this.evaluateWidgetWeekEvents();
            this.evaluateWidgetUpcomingEvents();
          },
          error: (error) => {
            console.error('Error creating calendar event: ', error);
            }
          })
      },
      createEventSeriesWithOverrides(event: CalendarEventUpdateRequest, overrideEvents: Date[]){
        api.createOrUpdateCalendarEvent(event, null, '', false, false, false)
        .pipe(
          take(1),
        )
        .subscribe({
          next: (events) => {
            overrideEvents.forEach((overrideDate) => {
              const updatedEvent = 
                createCalendarEventResourceSeriesOverrideEvent({...events[0]}, overrideDate);
              this.createEvent(mapCalendarEventResourceToCalendarEventUpdateRequest(updatedEvent));
            });
          },
          error: (error) => {
            console.error('Error creating calendar event: ', error);
            }
          })
      },
      updateEvent(event: CalendarEventUpdateRequest, eventId: number, overrideDetails: boolean, overrideGuests: boolean, overrideTiming: boolean ){
        api.createOrUpdateCalendarEvent(event, eventId, store.events().find(event => event.id === eventId)!.etag, overrideDetails, overrideGuests, overrideTiming)
        .pipe(
          take(1),
        )
        .subscribe({
          next: (events) => {
            this.updateEvents(events);
            if (event.override.parent_id !== null) {
              this.refreshVisibleSchedulerRangeForSelectedCalendars();
            }
            this.evaluateWidgetWeekEvents();
            this.evaluateWidgetUpcomingEvents();
          },
          error: (error) => {
            console.error('Error updating calendar event: ', error);
            }
          })
      },
      deleteEvent(eventID: number) {
        api.deleteCalendarEvent(eventID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.removeEvents([eventID, ...store.events().filter(e => e.override.parent_id === eventID).map(e => e.id)]);
            this.evaluateWidgetWeekEvents();
            this.evaluateWidgetUpcomingEvents();
          },
          error: (error) => {
            console.error('Error deleting calendar event: ', error);
          }
        });
      },
      deleteOccurenceFromSeries(eventID: number, occurrenceDate: Date) {
        const eventToModify = store.events().find(event => event.id === eventID);
        if(eventToModify) {
          const updatedEvent = {...eventToModify};
          updatedEvent.recurrence.deleted_occurrences = [
            ...updatedEvent.recurrence.deleted_occurrences,
            `${occurrenceDate.getUTCFullYear()}-${(occurrenceDate.getUTCMonth() + 1).toString().padStart(2, '0')}-${occurrenceDate.getUTCDate().toString().padStart(2, '0')}`
          ];
          this.updateEvent(mapCalendarEventResourceToCalendarEventUpdateRequest(updatedEvent), eventID, false, false, false);
        }
      },
      // #endregion events
      // #region preloading
      updatePreLoadedMonths(calendarId: number, start: Date, end: Date){
        const months: string[] = getMonthsBetweenDates(start, end)
        patchState(store, (state) => {
          const existingMonths = state.preLoadedMonths[calendarId] || [];
          const updatedMonths = [...existingMonths, ...months].filter((month, index, self) => 
            self.indexOf(month) === index
          );
          return {
            ...state,
            preLoadedMonths: {
              ...state.preLoadedMonths,
              [calendarId]: updatedMonths
            }
          };
        })
      },
      clearPreLoadedMonths(calendarId: number): void{
        if(store.preLoadedMonths()[calendarId]) {
          const updatedPreloadedMonths = {...store.preLoadedMonths()};
          delete updatedPreloadedMonths[calendarId];
          patchState(store, (state) => ({
            ...state,
            preLoadedMonths: updatedPreloadedMonths
          }));
        }
      },
      evaluatePreLoading(): void{
        const {start, end} = this.getVisibleSchedulerRange();
        const months = getMonthsBetweenDates(start, end);  
        
        // Check if all calendars have all required months loaded
        const allCalendarsLoaded = store.calendars().every((calendar) => {
          const preLoadedForCalendar = store.preLoadedMonths()[calendar.id] || [];
          return months.every((month) => preLoadedForCalendar.includes(month));
        });
        if (!allCalendarsLoaded) {
          // Check if some calendars have some months loaded to optimize loading
          const someLoaded = store.calendars().some((calendar) => {
            const preLoadedForCalendar = store.preLoadedMonths()[calendar.id] || [];
            return months.some((month) => preLoadedForCalendar.includes(month));
          });
          
          if (someLoaded) {
            // Find missing months across all calendars
            const allMissing = store.calendars().flatMap((calendar) => {
              const preLoadedForCalendar = store.preLoadedMonths()[calendar.id] || [];
              return months.filter(m => !preLoadedForCalendar.includes(m));
            });
            const uniqueMissing = [...new Set(allMissing)].sort();
            
            if (uniqueMissing.length > 0) {
              const firstMissing = new Date(uniqueMissing[0]);
              const lastMissing = new Date(uniqueMissing[uniqueMissing.length - 1]);
              this.loadCalendarEvents(firstMissing, getNextMonthEndDate(lastMissing));
            }
          } else {
            this.loadCalendarEvents(start, end);
          }
        }
      },
      checkForOutdatedEvent(calendarId: number, eventId: number, etag: string, isNewEvent: boolean): void {
        const event = store.events().find(e => e.id === eventId && e.calendar_id === calendarId);
        if(event && event.etag !== etag || isNewEvent) {
          this.clearPreLoadedMonths(calendarId);
          this.evaluatePreLoading();
        }
      },
      checkForEventToDelete(calendarId: number, eventId: number): void {
        const event = store.events().find(e => e.id === eventId && e.calendar_id === calendarId);
        if(event) {
          this.removeEvents([eventId]);
        }
      },
      // #endregion preloading
      // #region scheduler options
      setSelectedDate(date: Date){
        patchState(store, (state) => 
        ({
          ...state,
          schedulerOptions: {
            ...state.schedulerOptions,
            selectedDate: date
          }
        })
        );
        this.evaluatePreLoading();
      },
      setView(view: View){
        patchState(store, (state) => 
        ({
          ...state,
          schedulerOptions: {
            ...state.schedulerOptions,
            view: view
          }
        })
        );

        // Save view to cookie
        this.saveViewToCookie(view);
        
        this.evaluatePreLoading();
      },
      saveViewToCookie(view: View): void {
        document.cookie = `calendar_view=${view}; path=/; max-age=31536000; Secure; SameSite=Strict`;
      },
      getViewFromCookie(): string | null {
        const match = document.cookie.match(/(^|;\s*)calendar_view=([^;]*)/);
        return match ? decodeURIComponent(match[2]) : null;
      },
      // #endregion scheduler options
      // #region widget scheduler
      initWidgetScheduler(){
        this.resetWidgetScheduler();
        if (!capabilities.hasCalendarRight()) {
          return;
        }
        this.loadCalendars();
        patchState(store, (state) => ({
          ...state,
          translate: !!(profile.profile()?.user.auto_translation ?? false)
        }));
      },
      widgetWeekStartDate(): Date {
        return getStartOfWeek(store.widgetScheduler.selectedDate(), locale.firstDayOfWeekIndex());
      },
      widgetGoToPreviousWeek(){
        const newDate = this.widgetWeekStartDate();
        newDate.setDate(newDate.getDate() - 7);
        this.setWidgetSelectedDate(newDate);
      },
      widgetGoToNextWeek(){
        const newDate = this.widgetWeekStartDate();
        newDate.setDate(newDate.getDate() + 7);
        this.setWidgetSelectedDate(newDate);
      },
      setWidgetSelectedDate(date: Date){
        patchState(store, (state) => 
        ({
          ...state,
          widgetScheduler: {
            ...state.widgetScheduler,
            selectedDate: date
          }
        })
        );
        this.evaluateWidgetWeekEvents();
        this.evaluateWidgetUpcomingEvents();
      },
      evaluateWidgetWeekEvents(){
        store.calendars().forEach((calendar) => {
          api.getCalendarDatePriorities(
            calendar.id,
            this.widgetWeekStartDate().toISOString(),
            getEndOfWeek(this.widgetWeekStartDate(), locale.firstDayOfWeekIndex()).toISOString()
          ).pipe(take(1)).subscribe({
            next: (priorities) => {
              this.setWidgetWeekEvents(new Map(Object.entries(priorities)));
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
            }
          })
        });
      },
      setWidgetWeekEvents(priorities: Map<string, CalendarPrioritySumResource>){
        patchState(store, (state) => 
        ({
          ...state,
          widgetScheduler: {
            ...state.widgetScheduler,
            weekEvents: priorities
          }
        })
        );
      },
      evaluateWidgetUpcomingEvents(){
        store.calendars().forEach((calendar) => {
          api.getCalendarEventsForToday(calendar.id).pipe(take(1)).subscribe({
            next: (events) => {
              this.updateWidgetUpcomingEvents(events);
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
            }
          })
        });
      },
      updateWidgetUpcomingEvents(events: CalendarEventResource[]){

        patchState(store, (state) => ({
          ...state,
          widgetScheduler: {
            ...state.widgetScheduler,
            upcomingEvents: [...events]
          }
        }));
      },
      resetWidgetScheduler(){
        patchState(store, (state) => 
        ({
          ...state,
          widgetScheduler: initialState.widgetScheduler
        })
        );
      },
      // #endregion widget scheduler
    }
  }),
  withComputed((store) => {
    const language: LanguageService = inject(LanguageService);
    const locale: LocaleService = inject(LocaleService);

    return {
      // #region scheduler
      schedulerCalendars: computed(() => {
        const mappedData = store.calendars().map((calendar) => {
          return {
            CalendarText: calendar.name || 'Calendar',
            CalendarId: calendar.id.toString(),
            CalendarColor: '#c43081',
          }
        });
        return mappedData;
      }),
      schedulerQuery: computed(():Query | undefined => {
        if(store.selectedCalendarID() !== null) {
          return new Query().where('CalendarId', 'equal', store.selectedCalendarID()!.toString());
        } else {
          return undefined;
        }
      }),
      schedulerEventSettings: computed((): EventSettingsModel => {
        return {
          dataSource: [...store.events()].map(e => mapCalendarEventResourceToSyncfusionEvent(e, store.translate())),
          fields: {
            id: 'Id',
            subject: {name: 'Subject'},
            startTime: {name: 'StartTime'},
            endTime: {name: 'EndTime'},
            startTimezone: {name: 'StartTimezone'},
            endTimezone: {name: 'EndTimezone'},
            location: {name: 'Location'},
            description: {name: 'Description'},
            recurrenceRule: {name: 'RecurrenceRule'},
            recurrenceException: {name: 'RecurrenceException'},
            recurrenceID: {name: 'RecurrenceID'},
          }
        };
      }),
      // #endregion scheduler
      // #region widget
      widgetSchedulerEvents: computed((): TYPE.WidgetCalendarEvent[] => {
        const start = getStartOfWeek(store.widgetScheduler.selectedDate(), locale.firstDayOfWeekIndex());
        const mappedData: TYPE.WidgetCalendarEvent[] = [];

        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(start);
          dayDate.setDate(start.getDate() + i);

          const label = dayDate.toLocaleDateString(language.getLanguage(), {weekday: 'short'});
          const day = dayDate.getDate();

          const dateKey = dayDate.getFullYear() + '-' + String(dayDate.getMonth() + 1).padStart(2, '0') + '-' + String(dayDate.getDate()).padStart(2, '0');
          
          const event = store.widgetScheduler.weekEvents().get(dateKey) || {low: 0, medium: 0, high: 0};

          const eventDots: CalendarEventPriority[] = [];
          for (let i = 0; i < event.high; i++) {
            eventDots.push('high');
          }
          for (let i = 0; i < event.medium; i++) {
            eventDots.push('medium');
          }
          for (let i = 0; i < event.low; i++) {
            eventDots.push('low');
          }
          
          mappedData.push({
            date: dayDate,
            label,
            day,
            event: store.widgetScheduler.weekEvents().get(dateKey) || {low: 0, medium: 0, high: 0},
            eventDots: eventDots.slice(0, 3)
          });
        }

        return mappedData;
      }),
      widgetSchedulerMonth: computed(() => {
        return store.widgetScheduler.selectedDate().toLocaleDateString(language.getLanguage(), {month: 'short'});
      })
      // #endregion widget
    }
  })
);
